import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, GraduationCapIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { readMatchParams, readProfileParams } from '../utils/matchParams';
import { sanitizeTcpaHtml } from '../utils/sanitizeHtml';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ACCENT, SelectField, StageProgress, StepCard } from '../components/form/FormStepUI';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { TrackingNotice } from '../components/TrackingNotice';
import { trackLeadConversion } from '../utils/conversion';
import { areaFromProgram, clicksSearch, leadsSearch, tcpaPost, tcpaSearch, type ContactProfile, type MMSchool, type SearchProfile, type TCPAResult } from '../services/mediaMatchers';
// Step option lists
// Labels match the partner's value mapping, so the codes sent are unambiguous.
const educationLevels = ['High School Diploma / GED', 'Some High School', 'Some College', "Associate's Degree", "Bachelor's Degree", "Master's Degree", 'Doctorate'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  // Anything older than 60 years is rejected by the partner, so the list stops
  // there and collapses into a single "Before 1965" option.
  for (let y = currentYear + 2; y >= currentYear - 60; y--) {
    years.push(String(y));
  }
  years.push('Before 1965');
  return years;
}
const gradYearOptions = getGradYearOptions();
const startTimeframes = ['Immediately', '1–3 months', '3–6 months', '6–12 months', 'More than a year', 'Undecided'];
export function RequestInformationForm() {
  usePageMeta('Request Information | Find Online Degree Programs', 'Complete a quick form to get matched with accredited online degree programs.');
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state || {}) as {
    degreeInterest?: string;
    areaOfStudy?: string;
    specialization?: string;
  };
  // Fall back to URL params so a direct anchor link lands here pre-filled.
  const urlPrefill = readMatchParams();
  const profilePrefill = readProfileParams();
  const prefill = {
    degreeInterest: navState.degreeInterest || urlPrefill.degreeInterest,
    // The area is derivable from the program id when it isn't given.
    areaOfStudy: navState.areaOfStudy || urlPrefill.areaOfStudy || areaFromProgram(urlPrefill.specialization),
    specialization: navState.specialization || urlPrefill.specialization,
    ...profilePrefill
  };
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep3
  } = useFormState(prefill);
  const [step, setStep] = useState(1);
  const [zipLoading, setZipLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepError, setStepError] = useState<string>('');
  // Mounts the LeadiD and TrustedForm scripts, so their hidden inputs are
  // populated before any Media Matchers call fires.
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();

  // --- Media Matchers state -------------------------------------------------
  /** The PII step. The searches must resolve before it renders. */
  const CONTACT_STEP = 5;
  /** Location — the last step that contributes to the search payload. */
  const LOCATION_STEP = 4;
  const [leads, setLeads] = useState<MMSchool[] | null>(null);
  const [tcpa, setTcpa] = useState<TCPAResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tcpaFailed, setTcpaFailed] = useState(false);
  const searchFired = useRef(false);
  const buildProfile = useCallback((): SearchProfile => ({
    degreeInterest: formData.degreeInterest,
    areaStudy: formData.areaOfStudy,
    mProgram: formData.specialization,
    currentEducation: formData.currentEducation,
    gradYear: formData.gradYear,
    startTimeframe: formData.startDate,
    militaryAffiliation: formData.isMilitary,
    zipCode: formData.zipCode.replace(/\D/g, ''),
    address: formData.address,
    city: formData.city,
    state: formData.state
  }), [formData]);
  const runSearches = useCallback(async () => {
    setSearchLoading(true);
    setTcpaFailed(false);
    const profile = buildProfile();
    // Both fire in parallel — neither depends on the other.
    const [leadsResult, tcpaResult] = await Promise.all([leadsSearch(profile), tcpaSearch(profile)]);
    setLeads(leadsResult || []);
    setTcpa(tcpaResult);
    if (!tcpaResult) setTcpaFailed(true);
    setSearchLoading(false);
  }, [buildProfile]);

  // Safety net: if the visitor reaches the PII step without the searches
  // having run (a restored session, say), fire them on arrival.
  useEffect(() => {
    if (step !== CONTACT_STEP || searchFired.current) return;
    searchFired.current = true;
    runSearches();
  }, [step, runSearches]);
  const retrySearches = () => {
    searchFired.current = true;
    runSearches();
  };
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
        }
      }
    } catch {
      /* ignore */
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const goBack = () => {
    setStepError('');
    if (step === 1) {
      navigate('/get-matched');
    } else {
      setStep(step - 1);
    }
  };
  const goNext = () => {
    setStepError('');
    // Per-step validation
    if (step === 1 && !formData.currentEducation) {
      setStepError('Please select your highest level of education.');
      return;
    }
    if (step === 2 && !formData.gradYear) {
      setStepError('Please select your graduation year.');
      return;
    }
    if (step === 3 && !formData.startDate) {
      setStepError('Please select when you would like to start.');
      return;
    }
    if (step === LOCATION_STEP) {
      if (!formData.address.trim()) {
        setStepError('Please enter your street address.');
        return;
      }
      if (!formData.zipCode.trim() || formData.zipCode.replace(/\D/g, '').length < 5) {
        setStepError('Please enter a valid 5-digit ZIP code.');
        return;
      }
      // Location is the last input the search payload needs, so both Searches
      // fire here — before the PII step renders — and the TCPA disclaimer is
      // already in hand when the consent checkbox appears.
      if (!searchFired.current) {
        searchFired.current = true;
        runSearches();
      }
    }
    setStep(step + 1);
  };
  const handleSubmit = async () => {
    setStepError('');
    if (!validateUniStep3()) return;
    // Without the TCPA record there is no consent to post against.
    if (!tcpa) {
      setTcpaFailed(true);
      setStepError('We could not load the consent details. Please retry before submitting.');
      return;
    }
    setIsSubmitting(true);
    const profile = buildProfile();
    const contact: ContactProfile = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: stripPhone(formData.phone)
    };
    // TCPA Post first, then Clicks Search excluding the brand that just took
    // consent, so the carousel has results ready.
    const posted = await tcpaPost(tcpa.mRecordID, contact);
    // Conversion fires on confirmation, not on the click.
    if (posted) trackLeadConversion({
      source: 'tcpa'
    });
    const clicks = await clicksSearch(profile, contact, tcpa.mBrandID);
    setIsSubmitting(false);
    navigate('/matches', {
      state: {
        leads: leads || [],
        clicks: clicks || [],
        profile,
        contact,
        disclosure: tcpa.disclosure,
        excludeBrand: tcpa.mBrandID
      }
    });
  };
  // Card content for each step
  const renderStepCard = () => {
    if (step === 1) {
      return <StepCard title="What is your highest level of education?" onContinue={goNext} ctaLabel="Continue" info="Your education background helps schools understand where you are in your academic journey and recommend the right program level." error={stepError} data-id="element-2709" data-mp="1hbxky77pulcy">
          <SelectField value={formData.currentEducation} onChange={v => updateFormData('currentEducation', v)} options={educationLevels.map(l => ({
          label: l,
          value: l
        }))} data-id="element-2710" data-mp="3bkb44jyr2pmh" />
        </StepCard>;
    }
    if (step === 2) {
      return <StepCard title="When did you graduate high school?" onContinue={goNext} ctaLabel="Continue" info="Knowing when you graduated helps us verify eligibility and connect you with programs that fit your timeline." error={stepError} data-id="element-2711" data-mp="2xuhgu62w8dev">
          <SelectField value={formData.gradYear} onChange={v => updateFormData('gradYear', v)} options={gradYearOptions.map(y => ({
          label: y,
          value: y
        }))} data-id="element-2712" data-mp="3lgnxthuap3h8" />
        </StepCard>;
    }
    if (step === 3) {
      return <StepCard title="When would you like to start classes?" onContinue={goNext} ctaLabel="Continue" info="Your preferred start date helps us find programs with upcoming enrollment windows that work for your schedule." error={stepError} data-id="element-2713" data-mp="25624jrb5c978">
          <SelectField value={formData.startDate} onChange={v => updateFormData('startDate', v)} options={startTimeframes.map(t => ({
          label: t,
          value: t
        }))} data-id="element-2714" data-mp="gs5j1aty6c2x" />
        </StepCard>;
    }
    if (step === 4) {
      return <StepCard title="Where are you located?" onContinue={goNext} ctaLabel="Continue" info="Your location helps us find programs available in your area and determine eligibility for state-specific financial aid." error={stepError} data-id="element-2715" data-mp="zox49pt1v19m">
          <div className="space-y-5" data-id="element-2716" data-mp="2xeom60orrehr">
            <div data-id="element-2717" data-mp="2xfq7cxzi1hd7">
              <label htmlFor="address" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2718" data-mp="2xg9h3rezwzg5">
                Street Address{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2719" data-mp="2rba7htorojik">
                  *
                </span>
              </label>
              <input id="address" type="text" value={formData.address} onChange={e => updateFormData('address', e.target.value)} placeholder="123 Main St" className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-0" style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2720" data-mp="2ij887yqfp99v" />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" style={{
            backgroundColor: "#FAFBFE"
          }} data-id="element-2721" data-mp="3tz6lyfwk4jhf">
              <label htmlFor="zipCode" className="flex items-center gap-2 text-sm font-bold text-gray-800 mb-2" data-id="element-2722" data-mp="3rl1kqq5tmnm3">
                <span data-id="element-2723" data-mp="3nhve3hesquf3">📍</span>
                Enter your ZIP Code
              </label>
              <div className="relative" data-id="element-2724" data-mp="3ij207nvll1ee">
                <input id="zipCode" type="text" value={formData.zipCode} onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                updateFormData('zipCode', val);
                if (val.length === 5) lookupZip(val);
              }} placeholder="e.g. 90210" maxLength={5} className="block w-full px-4 py-3 text-base border-2 rounded-md bg-white focus:outline-none" style={{
                borderColor: ACCENT
              }} data-id="element-2725" data-mp="3ik3mkoi2dabf" />
                {zipLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-2726" data-mp="ei958s5utmq">
                    <LoaderIcon className="w-4 h-4 animate-spin text-gray-500" data-id="element-2727" data-mp="ce7kxhqi6pm" />
                  </div>}
              </div>
              {formData.city && formData.state && <p className="text-xs text-gray-600 mt-2" data-id="element-2728" data-mp="t24byetkdu8j">
                  📌 {formData.city}, {formData.state}
                </p>}
            </div>
          </div>
        </StepCard>;
    }
    if (step === 5) {
      const locationLine = formData.city && formData.state ? `${formData.city}, ${formData.state}` : '';
      return <StepCard title="Last step! Where should we send your matches?" subtitle={`We'll match you with top programs in ${locationLine}.`} onContinue={handleSubmit} ctaLabel={isSubmitting ? 'Submitting...' : 'See Matching Colleges'} ctaIcon={<GraduationCapIcon className="w-5 h-5 ml-2 inline-block" data-id="element-2730" data-mp="1t2pz5doexi2t" />} isSubmitting={isSubmitting || searchLoading} error={stepError} data-id="element-2729" data-mp="1fb1qjkddhbql">
          <div className="space-y-5" data-id="element-2731" data-mp="1y19nfkqhb5yc">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2732" data-mp="230w1fwe4sq87">
              <div data-id="element-2733" data-mp="232qk141xk694">
                <label htmlFor="firstName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2734" data-mp="22z2ra74b7nvg">
                  First Name{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-2735" data-mp="34p9c37gl0rfq">
                    *
                  </span>
                </label>
                <input id="firstName" type="text" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} placeholder="John" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.firstName ? 'border-red-400' : 'border-gray-300'}`} style={{
                '--tw-ring-color': ACCENT
              } as React.CSSProperties} data-id="element-2736" data-mp="3dlid2qk7jy3z" />
                {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-2737" data-mp="54nhzx9nji0g">
                    {errors.firstName}
                  </p>}
              </div>
              <div data-id="element-2738" data-mp="dn92iydbeqoq">
                <label htmlFor="lastName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2739" data-mp="hm85idfhkddj">
                  Last Name{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-2740" data-mp="8ls2t66kha9q">
                    *
                  </span>
                </label>
                <input id="lastName" type="text" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} placeholder="Doe" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.lastName ? 'border-red-400' : 'border-gray-300'}`} style={{
                '--tw-ring-color': ACCENT
              } as React.CSSProperties} data-id="element-2741" data-mp="11ah4b6bjbhm0" />
                {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-2742" data-mp="1oyvx6jj8b3st">{errors.lastName}</p>}
              </div>
            </div>

            <div data-id="element-2743" data-mp="1k0ucxva2gyfr">
              <label htmlFor="email" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2744" data-mp="1fx64ladm1ytg">
                Email Address{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2745" data-mp="1aze4vu6q58ep">
                  *
                </span>
              </label>
              <input id="email" type="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} placeholder="john@example.com" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.email ? 'border-red-400' : 'border-gray-300'}`} style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2746" data-mp="118t35xvr7nrk" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-2747" data-mp="37xbkt8153utv">{errors.email}</p>}
            </div>

            <div data-id="element-2748" data-mp="3ctith8kx7kfi">
              <label htmlFor="phone" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2749" data-mp="2g5cm4dwm6dxj">
                Phone Number{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2750" data-mp="2k4v5344vkexq">
                  *
                </span>
              </label>
              <input id="phone" type="tel" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} placeholder="555-123-4567" maxLength={13} className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.phone ? 'border-red-400' : 'border-gray-300'}`} style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2751" data-mp="2ty27cg4bjetl" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-2752" data-mp="1dqozblsxcyrb">{errors.phone}</p>}
            </div>

            {/* Consent language comes from the TCPA Search response, so the
                text shown is the text the certificate records. */}
            {searchLoading ? <div className="flex items-center gap-2 text-sm text-gray-500 pt-2" data-id="element-2753" data-mp="1ijrjj3btavuu">
                <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2754" data-mp="1whyb61q63zm2" />
                Loading consent details...
              </div> : tcpaFailed ? <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-id="element-2755" data-mp="1rhbn4ix5d48m">
                <p className="text-sm text-red-800 font-medium" data-id="element-2756" data-mp="2b5pyg2mg3z45">
                  We couldn't load the consent details.
                </p>
                <p className="text-xs text-red-600 mt-1" data-id="element-2757" data-mp="224iaoehk2gob">
                  This is required before we can submit your request.
                </p>
                <button type="button" onClick={retrySearches} className="mt-3 inline-flex items-center px-4 py-2 rounded-md bg-white border border-red-300 text-sm font-bold text-red-700 hover:bg-red-100 transition-colors duration-200" data-id="element-2758" data-mp="264sf3mpp8z12">
                  Retry
                </button>
              </div> : <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-1 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0 accent-[#1668E0]" data-id="element-2759" data-mp="1ngnf8qc5xy7j">
                {/* tcpaDisclaimer is partner-supplied HTML, so it is
                    sanitized rather than rendered as plain text. */}
                {tcpa?.disclosure ? <span dangerouslySetInnerHTML={{
              __html: sanitizeTcpaHtml(tcpa.disclosure)
            }} data-id="element-2760" data-mp="13mxbkx50rclu" /> : `I acknowledge that, by clicking the checkbox as my official
                signature, I consent to representatives of OnlineCollegeGuide and
                our Partner Schools contacting me about educational opportunities
                via email, text, or phone number(s), including through the use of
                an automatic dialer, at the number and email I provided. Message
                and data rates may apply. I understand that my consent is not a
                requirement to enroll, and I may withdraw my consent at any time.`}
              </TCPADisclosure>}
          </div>
        </StepCard>;
    }
    return null;
  };
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-2761" data-mp="2pcazcxs7thyt">
      <Header data-id="element-2762" data-mp="334h65t9hacll" />
      <main className="flex-1 py-6 sm:py-10 px-4" data-id="element-2763" data-mp="33365i2bbxkyp">
        <div className="max-w-3xl mx-auto" data-id="element-2764" data-mp="2y54khbucfbkl">
          {/* Stage progress. Step 1 of the funnel is program selection on the
              start page, so the questionnaire begins at step 2. */}
          <StageProgress currentStep={step + 1} data-id="element-2765" data-mp="2ahd7e60zaui9" />

          {/* Back button */}
          <div className="mt-2 mb-4" data-id="element-2766" data-mp="26frmpyi7dbxi">
            <button type="button" onClick={goBack} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors" data-id="element-2767" data-mp="2kdgbvijaz58d">
              <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2768" data-mp="3vs3jht8icvln" />
              Back
            </button>
          </div>

          {/* Step card */}
          <AnimatePresence mode="wait" data-id="element-2769" data-mp="3qtjybv1qnnuc">
            <motion.div key={step} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2
          }} data-id="element-2770" data-mp="2pdh891sovag">
              {renderStepCard()}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8" data-id="element-2771" data-mp="3k5btu1vvdiqd">
            <TrackingNotice data-id="element-2772" data-mp="3k6d9ykrqsvey" />
          </div>
        </div>
      </main>
      <Footer data-id="element-2773" data-mp="3p46bv1m0j39f" />
    </div>;
}